import { useState, useContext, useEffect } from 'react';
import { Button, CircularProgress, Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCheckmark } from 'react-icons/io5';
import { FiEyeOff, FiEye } from 'react-icons/fi';

import { ToastNotifications } from 'src/components';
import { GoldMindsLogo } from 'src/images';
import { MyContext } from 'src/context';
import { TextField } from 'src/components';
import { loginValidationSchema } from 'src/validations/teacher';
import { authService } from '../services/auth/auth.service';
import { ResetPasswordBg1, ResetPasswordBg2, ResetPasswordBg3, ResetPasswordBg4 } from 'src/images';

import './styles/login.css';
import Page from 'src/components/Page';

interface LoginFormValues {
  email: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setUser } = useContext(MyContext);
  const [check, setCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState('');

  useEffect(() => {
    const images = [ResetPasswordBg1, ResetPasswordBg2, ResetPasswordBg3, ResetPasswordBg4];
    const randomImage = images[Math.floor(Math.random() * images.length)];
    setBackgroundImage(randomImage);
  }, []);

  // Show session expired message if redirected due to expired token
  useEffect(() => {
    if (searchParams.has('session_expired')) {
      ToastNotifications.info('Your session has expired. Please sign in again.');
      // Remove the query param to prevent showing the message on refresh
      searchParams.delete('session_expired');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (values: LoginFormValues, resetForm: () => void) => {
    const { email, password } = values;

    try {
      setLoading(true);

      // Attempt to sign in with Supabase
      const data = await authService.signIn(email, password);
   

      if (data?.user) {
        // Get user profile from your users table
        const userData  = await authService.getCurrentUser(data?.user?.id);

        const userWithRole = {
          ...data.user,
          role: userData.role || 'STUDENT',
        };

        // Update context and local storage
        setUser(userWithRole);
        localStorage.setItem('user', JSON.stringify(userWithRole));

        // Navigate based on role
        const roleRoutes = {
          ADMIN: '/admin/dashboard',
          TEACHER: '/teacher/dashboard',
          STUDENT: '/student/dashboard',
        } as const;

        const userRole = userData.role?.name?.toUpperCase();
        const route = roleRoutes[userRole as keyof typeof roleRoutes] || '/login';

        // add delay of 1 second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        navigate(route, { replace: true });
        resetForm();
        ToastNotifications.success('Login successful');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      ToastNotifications.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      await handleLogin(values, resetForm);
    },
  });

  return (
    <div className="_login_container">
      {/* @ts-ignore */}
      <Page title="Login">
        <Grid container justifyContent="center">
          <Grid sm={12} xs={12} md={6} lg={6} xl={6}>
            <div className="loginFirstSec">
              <img src={GoldMindsLogo} alt="Gold Minds Logo" width={120} height={120} />
              <h1>Login to your account</h1>
              <p className="loginDes">Enter the details below to login to your account</p>
              <form onSubmit={formik.handleSubmit}>
                <div style={{ marginTop: 20, marginBottom: 20 }}>
                  <TextField
                    label="Email"
                    placeholder="Enter Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    name="email"
                    // @ts-ignore
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                  <TextField
                    label="Password"
                    placeholder="Enter password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    // @ts-ignore

                    error={Boolean(formik.touched.password && formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    labelStyle={{ marginTop: 20 }}
                    right={
                      showPassword ? (
                        <FiEye color="#718096" size={20} onClick={() => setShowPassword(!showPassword)} />
                      ) : (
                        <FiEyeOff onClick={() => setShowPassword(!showPassword)} color="#718096" size={20} />
                      )
                    }
                  />
                  <div className="forgotPwdSec">
                    {/* <div className="rememberRow">
                      <div
                        onClick={() => setCheck(!check)}
                        className="remmberCheckbox"
                        style={{ backgroundColor: check ? '#297041' : '#fff' }}
                      >
                        {check && <IoCheckmark size={15} color="#fff" />}
                      </div>
                      <span>Remember me</span>
                    </div> */}
                    <span className="forgotPasswordTxt" onClick={() => navigate('/forgot-password')}>
                      Forgot Password?
                    </span>
                  </div>
                </div>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  style={{ height: 55, marginTop: 20 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress color="inherit" size={20} /> : 'Sign in'}
                </Button>
              </form>
              <div className="dontHaveAnAccountSec">
                Don't have an account?{' '}
                <span className="forgotPasswordTxt" onClick={() => navigate('/register')}>
                  Get Started
                </span>
              </div>
            </div>
          </Grid>
          <Grid sm={12} xs={12} md={6} lg={6} xl={6}>
            <div className="loginSecondSec">
              <img src={backgroundImage} alt="login background" className="loginBgImage" />
            </div>
          </Grid>
        </Grid>
      </Page>
    </div>
  );
}
